import {
  EXHIBITION_STORAGE_KEY,
  ExhibitionSessionState,
} from "models/ExhibitionSession";

/**
 * What a visitor may do in an exhibition session: everything.
 *
 * There used to be two lists here — tools forbidden outright, and tools blocked
 * while the shared showcase was on screen — and the assistant was told about
 * them in its prompt. Between them they turned "print the insoles" into "that
 * is not available in this mode", because the print flow starts by asking which
 * printers are set up and that read was filed under printer configuration.
 *
 * The rule is simpler than any list: an exhibition session differs from an
 * ordinary one in exactly one way, which is that its first design is narrated.
 * Once the narration has finished the visitor is an ordinary user on an
 * ordinary design, and asking to print has to build real G-code like anywhere
 * else. The visitor never reaches the showcase design itself either — the
 * server hands them a fresh copy of it on every entry and never puts them on
 * the original — so the read-only boundary was guarding a design they could not
 * get to, at the cost of the ones they could.
 */

/**
 * Whether the guided tour belongs to the design currently on screen.
 *
 * The tour narrates the copy of the showcase this entry was issued. Its
 * pressure heat map and WBCT reveals only have data to show on that design, and
 * more to the point a design the visitor went on to create is their own work,
 * not a demonstration — so it gets the ordinary assistant, with nothing
 * scripted on top.
 */
export function isExhibitionTourDesign(
  selectedDesignId: number,
  demoDesignId?: number
): boolean {
  return Boolean(demoDesignId && demoDesignId > 0 && selectedDesignId === demoDesignId);
}

/**
 * Whether the design being loaded is the one the narration runs on.
 *
 * Read from storage rather than from React state because the viewer needs the
 * answer while it is building the scene, before any effect has run. The
 * narration opens on a plain shell — no skeleton, no shoe, no lifted insole —
 * and reveals each of those as it talks about them, so the loader must not
 * switch them on in the first place. Hiding them again afterwards showed them
 * for as long as the design took to load.
 */
export function isExhibitionNarrationDesign(selectedDesignId: number): boolean {
  if (typeof localStorage === "undefined") return false;
  const raw = localStorage.getItem(EXHIBITION_STORAGE_KEY);
  if (!raw) return false;
  try {
    const session = JSON.parse(raw) as ExhibitionSessionState;
    return isExhibitionTourDesign(selectedDesignId, session.demoDesignId);
  } catch {
    return false;
  }
}
